import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  QUOTE_HISTORY_STATUSES,
  QUOTE_STATUS_LABELS,
  type QuoteStatusFilter,
} from '@/entities/quote';
import { Button, FilterSelect, PageHeader, SearchInput } from '@/shared/ui';
import { QuoteList } from '@/widgets/quote-list';

/** 사이드바의 두 메뉴(2026-10-20 결정) — 견적 요청(처리할 건)과 견적 내역(그 나머지). 상세는 어디서 열든 같다. */
type QuotesPageView = 'requests' | 'history';

interface QuotesPageProps {
  readonly view: QuotesPageView;
}

const VIEWS: Readonly<
  Record<
    QuotesPageView,
    { readonly title: string; readonly description: string; readonly filter: QuoteStatusFilter }
  >
> = {
  requests: {
    title: '견적 요청',
    description: '검토 대기·수정 요청 — 운영자가 처리할 견적입니다.',
    filter: 'DEFAULT',
  },
  history: {
    title: '견적 내역',
    description: '발행한 뒤의 견적입니다 — 주문·거절·반려·취소·만료까지.',
    filter: 'HISTORY',
  },
};

/** 견적 내역 상태 필터 선택값 → 필터. '전체'(HISTORY)와 내역 상태 하나만 받고 그 밖은 무시한다. */
const historyFilterOf = (value: string): QuoteStatusFilter | undefined =>
  value === 'HISTORY' ? 'HISTORY' : QUOTE_HISTORY_STATUSES.find((status) => status === value);

/**
 * 어드민 견적 목록(/quotes 견적 요청, /quotes/history 견적 내역 — 견적 설계 §13) — 상태 필터·검색어·페이지 상태만 소유하고
 * 표는 위젯이 담당한다. 견적 요청은 상태가 둘뿐이라 필터가 없고, 견적 내역만 상태 필터(전체 + 상태 하나)를 둔다.
 * 검색은 검색 버튼으로만 적용한다 — 목록 조회마다 서버가 접속 기록(PII_ACCESSED)을 남기므로 입력마다 보내지 않는다.
 * 셸(헤더 · 사이드바)은 레이아웃 라우트(app/admin-layout)가 제공한다.
 */
export const QuotesPage = ({ view }: QuotesPageProps): ReactElement => {
  const navigate = useNavigate();
  const current = VIEWS[view];
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<QuoteStatusFilter>(current.filter);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  return (
    <>
      <PageHeader title={current.title} description={current.description} />

      <form
        aria-label="견적 검색"
        className="flex min-w-0 flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
          setPage(1);
        }}
      >
        {view === 'history' ? (
          <FilterSelect
            aria-label="상태 필터"
            value={statusFilter}
            onChange={(event) => {
              const next = historyFilterOf(event.target.value);
              if (next) {
                setStatusFilter(next);
                setPage(1);
              }
            }}
          >
            <option value="HISTORY">전체</option>
            {QUOTE_HISTORY_STATUSES.map((status) => (
              <option key={status} value={status}>
                {QUOTE_STATUS_LABELS[status].label}
              </option>
            ))}
          </FilterSelect>
        ) : null}
        <SearchInput
          aria-label="검색어"
          placeholder="견적 번호·회사·단체명·담당자명"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <Button type="submit" variant="outline" className="h-10 px-5">
          검색
        </Button>
      </form>

      <QuoteList
        page={page}
        statusFilter={statusFilter}
        search={search}
        onPageChange={setPage}
        onOpen={(quoteRequestId) => {
          void navigate(`/quotes/${quoteRequestId}`);
        }}
      />
    </>
  );
};
